import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScrollAnimation } from '@/hooks/useScrollAnimation';
import type { HomepageSection } from '@/lib/supabase-types';
import massaggi640 from '@/assets/massaggi-section-640.webp';
import massaggi1280 from '@/assets/massaggi-section-1280.webp';
import massaggi1920 from '@/assets/massaggi-section-1920.webp';
import tantra640 from '@/assets/tantra-section-640.webp';
import tantra1280 from '@/assets/tantra-section-1280.webp';
import tantra1920 from '@/assets/tantra-section-1920.webp';
import corsi640 from '@/assets/corsi-section-640.webp';
import corsi1280 from '@/assets/corsi-section-1280.webp';
import corsi1920 from '@/assets/corsi-section-1920.webp';
import eventi640 from '@/assets/eventi-section-640.webp';
import eventi1280 from '@/assets/eventi-section-1280.webp';
import eventi1920 from '@/assets/eventi-section-1920.webp';

type Variant = { src: string; srcSet: string };
const make = (s640: string, s1280: string, s1920: string): Variant => ({
  src: s1280,
  srcSet: `${s640} 640w, ${s1280} 1280w, ${s1920} 1920w`,
});

const sectionImages: Record<string, Variant> = {
  massaggi: make(massaggi640, massaggi1280, massaggi1920),
  tantra: make(tantra640, tantra1280, tantra1920),
  corsi: make(corsi640, corsi1280, corsi1920),
  eventi: make(eventi640, eventi1280, eventi1920),
};

interface ServiceSectionProps {
  section: HomepageSection;
  reverse?: boolean;
}

export function ServiceSection({ section, reverse = false }: ServiceSectionProps) {
  // Use section image from DB, fallback to mapped image, then default
  const variant = sectionImages[section.section_key] || sectionImages.massaggi;
  const imageUrl = section.image_url || variant.src;
  const srcSet = section.image_url ? undefined : variant.srcSet;

  return (
    <section className="relative w-full min-h-[90vh] md:min-h-[110vh] overflow-hidden">
      {/* Background image with Ken Burns animation */}
      <div className="absolute inset-0">
        <img
          src={imageUrl}
          srcSet={srcSet}
          sizes="100vw"
          width={1920}
          height={1080}
          alt={section.title || ''}
          className="w-full h-full object-cover animate-ken-burns"
          loading="lazy"
          decoding="async"
        />
        {/* Gradient overlay for readability (alternates side based on reverse) */}
        <div
          className={`absolute inset-0 ${
            reverse
              ? 'bg-gradient-to-l from-background/95 via-background/60 to-background/20'
              : 'bg-gradient-to-r from-background/95 via-background/60 to-background/20'
          }`}
        />
      </div>

      {/* Content overlay */}
      <div className="relative z-10 container mx-auto h-full px-6 md:px-12 flex items-center">
        <ScrollAnimation
          animation={reverse ? 'fade-left' : 'fade-right'}
          className={`w-full md:w-1/2 space-y-5 ${
            reverse ? 'md:ml-auto md:text-right' : 'text-left'
          }`}
        >
          <h2 className="text-5xl md:text-7xl font-bold text-gradient-coral tracking-tight">
            {section.title}
          </h2>
          {section.subtitle && (
            <p className="text-lg md:text-xl text-primary font-medium">
              {section.subtitle}
            </p>
          )}
          {section.content && (
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
              {section.content}
            </p>
          )}
          {section.cta_url && section.cta_text && (
            <div
              className={`pt-4 flex flex-wrap gap-3 ${
                reverse ? 'md:justify-end' : 'justify-start'
              }`}
            >
              <Button
                asChild
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 hover:scale-105 hover:shadow-lg"
              >
                <Link to={section.cta_url}>{section.cta_text}</Link>
              </Button>
            </div>
          )}
        </ScrollAnimation>
      </div>
    </section>
  );
}
